import { camelCase } from 'lodash';
import { ComponentProps, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CalendarIcon } from '@treams/treams-icons';
import {
	MeetingSeriesRecurrenceFrequency,
	MeetingTargetType,
	NoteFragment,
} from '@treams/treams-network';
import { Input } from '@treams/treams-ui';

import { MeetingModal } from '../../meetings/MeetingModal';

export function OneOnOneMeetingModal(props: {
	note: NoteFragment;
	subject: ComponentProps<typeof OneOnOneMeetingSeriesMetadataFields>['subject'];
	onClose: () => void;
}) {
	const { t } = useTranslation();

	const [defaultTitle, setDefaultTitle] = useState(
		props.note.meeting?.series?.noteSeriesMetadata?.defaultTitle ?? '',
	);

	const attendees = useMemo(() => {
		const collaborators = props.note.collaborators.edges.map((edge) => edge.node);
		const subject = props.note.subject;

		return subject && !collaborators.some((collaborator) => collaborator.id === subject.id)
			? [subject, ...collaborators]
			: collaborators;
	}, [props.note.collaborators, props.note.subject]);

	return (
		<MeetingModal
			title={t('oneOnOne.meeting.title')}
			onClose={props.onClose}
			target={{ type: MeetingTargetType.Note, id: props.note.id }}
			meeting={props.note.meeting}
			attendeesLabel={t('oneOnOne.meeting.attendees.label')}
			attendees={attendees}
			seriesMetadata={{ noteSeriesMetadata: { defaultTitle, subjectID: props.subject.id } }}
			renderSeriesMetadataFields={(frequency) => (
				<OneOnOneMeetingSeriesMetadataFields
					frequency={frequency}
					value={defaultTitle}
					onChange={setDefaultTitle}
					subject={props.subject}
					canUpdate={props.note.viewerCanUpdate}
				/>
			)}
			canUpdate={props.note.viewerCanUpdate}
		/>
	);
}

function OneOnOneMeetingSeriesMetadataFields(props: {
	frequency: MeetingSeriesRecurrenceFrequency;
	value: string;
	onChange: (value: string) => void;
	subject: Pick<NonNullable<NoteFragment['subject']>, 'firstName' | 'id'>;
	canUpdate: boolean;
}) {
	const { t } = useTranslation();

	const placeholder = useMemo(() => {
		const adjective = t(
			`meeting.series.recurrence.frequencyAdjective.${camelCase(props.frequency)}`,
		);

		const placeholder = t('oneOnOne.meeting.series.defaultTitle.placeholder', {
			adjective,
			subject: props.subject.firstName,
		});

		return placeholder.charAt(0).toUpperCase() + placeholder.slice(1);
	}, [props.frequency, props.subject, t]);

	// Maintain placeholder values if not yet user-configured (e.g. if the frequency changes).
	const lastPlaceholder = useRef<string | null>(null);
	useEffect(() => {
		if (props.value === lastPlaceholder.current || props.value === '') {
			props.onChange(placeholder);
		}

		lastPlaceholder.current = placeholder;
	}, [placeholder]);

	return (
		<Input
			small
			icon={<CalendarIcon />}
			maxWidth="calc(50% - 4px)"
			value={props.value === placeholder ? '' : props.value}
			onChange={(newValue) => props.onChange(newValue === '' ? placeholder : newValue)}
			label={t('oneOnOne.meeting.series.defaultTitle.label')}
			placeholder={placeholder}
			disabled={!props.canUpdate}
		/>
	);
}
